import CircularProgress from "@material-ui/core/CircularProgress";
import _ from "lodash";
import * as PropTypes from "prop-types";
import React, {useCallback, useEffect, useState} from "react";
import {toast} from "react-toastify";
import {FACHADMIN, SYSTEMADMIN} from "../../consts/funktionaleRollen";
import {globaleRollen} from "../../consts/valueLists";
import {getBetriebAuswahlEndpoint} from "../../services/BetriebeService";
import http, {handleApiException, isApiCancel, useHttp} from "../../services/HttpService";
import {getAlleOrganisationenEndpoint} from "../../services/OrganisationsService";
import {getLokaleRollenEndpoint} from "../../services/RollenService";
import {createCalculatedColumn, createColumn, createComponentColumn, createSelectRowComponentColumn} from "../../utils/ColumnUtils";
import {formatDate} from "../../utils/DateUtils";
import {useForm} from "../../utils/FormUtils";
import {userHasAnyRole} from "../../utils/SecurityUtils";
import {requiredEnumWerteListeObjekt, requiredString} from "../../utils/Validator";
import EAAccordion from "../EAAccordion";
import EAButton from "../EAButton";
import EAButtonBar from "../EAButtonBar";
import EADialog from "../EADialog";
import EATable from "../EATable";
import EATextField from "../EATextField";
import EATableActionButton from "../tableComponents/EATableActionButton";
import {useConfirmation} from "../ConfirmationDialog/ConfirmationDialogProvider";
import {confirmDeleteOption} from "../ConfirmationDialog/confirmationDialogOptions";
import {intersection, not} from "../../utils/ArrayUtils";
import EATransferList from "../EATransferList";
import {getRegionalstelleEndpoint} from "../../services/WertelistenService";
import {usePreformattedHttp} from "../../utils/usePreformattedHttp";
import EACheckbox from "../EACheckbox";
import "../../styles/components/rollenListe.css";

function TableDialog({open, data, formFactory, onClickOk, onClickCancel, betriebe, organisationen, regionalstellen, saving}) {
    const [selectedBetriebe, setSelectedBetriebe] = useState(betriebe.filter((betrieb) => data.betriebe.includes(betrieb.id)));
    const [filteredBetriebe, setFilteredBetriebe] = useState([]);
    const [filterBetriebe, setFilterBetriebe] = useState("");
    const [expandedBetriebe, setExpandedBetriebe] = useState(false);
    useEffect(() => {
        if (betriebe) {
            setFilteredBetriebe([
                ...betriebe.filter((item) => {
                    return !filterBetriebe || item?.bezeichnung?.toString().toLowerCase().includes(filterBetriebe.toLowerCase());
                }),
            ]);
        }
    }, [betriebe, filterBetriebe]);

    const [selectedOrganisationen, setSelectedOrganisationen] = useState(
        organisationen.filter((organisation) => data.organisationen.includes(organisation.id))
    );
    const [filteredOrganisationen, setFilteredOrganisationen] = useState([]);
    const [filterOrganisationen, setFilterOrganisationen] = useState("");
    const [expandedOrganisationen, setExpandedOrganisationen] = useState(false);
    useEffect(() => {
        if (organisationen) {
            setFilteredOrganisationen([
                ...organisationen.filter((item) => {
                    return (
                        !filterOrganisationen || item?.bezeichnung?.toString().toLowerCase().includes(filterOrganisationen.toLowerCase())
                    );
                }),
            ]);
        }
    }, [organisationen, filterOrganisationen]);

    const [selectedRegionalstellen, setSelectedRegionalstellen] = useState(
        regionalstellen.filter((regionalstelle) => data.regionalstelleIds.includes(regionalstelle.id))
    );
    const [filteredRegionalstellen, setFilteredRegionalstellen] = useState([]);
    const [filterRegionalstellen, setFilterRegionalstellen] = useState("");
    const [expandedRegionalstellen, setExpandedRegionalstellen] = useState(false);
    useEffect(() => {
        if (regionalstellen) {
            setFilteredRegionalstellen([
                ...regionalstellen.filter((item) => {
                    return (
                        !filterRegionalstellen ||
                        item?.gemeindeBezeichnung?.toString().toLowerCase().includes(filterRegionalstellen.toLowerCase())
                    );
                }),
            ]);
        }
    }, [regionalstellen, filterRegionalstellen]);

    return (
        <EADialog
            open={open}
            titleChildren={"Lokale Rolle " + (data.id ? "bearbeiten" : "erstellen")}
            content={
                <div className={"gridl"} style={{width: "800px"}}>
                    {(saving && <CircularProgress style={{justifySelf: "center"}}/>) || (
                        <div className="gridl" style={{gridTemplateColumns: "1fr 1fr"}}>
                            {formFactory.renderInput("bezeichnung", "bezeichnung", "Bezeichnung")}
                            {formFactory.renderSelect("globaleRolle", "globaleRolle", "Globale Rolle", globaleRollen, false, {
                                optionTitle: "value",
                                optionKey: "id",
                            })}
                            {formFactory.renderCheckbox("alleRegionalstellen", "alleRegionalstellen", "Alle Regionalstellen")}
                            <div style={{gridColumnEnd: "span 2"}}>
                                {" "}
                                <EAAccordion
                                    label={`Betriebe (${selectedBetriebe.length})`}
                                    wrapperClassName="betriebe"
                                    disabled={data.alleRegionalstellen}
                                    className="gridl"
                                    id={"betriebeAccordion"}
                                    variant="light"
                                    expanded={expandedBetriebe && !data.alleRegionalstellen}
                                    onChange={(e, expanded) => setExpandedBetriebe(expanded)}
                                >
                                    <EATextField
                                        id={"searchFieldBetriebe"}
                                        value={filterBetriebe}
                                        label={"Suche"}
                                        onChange={({currentTarget}) => setFilterBetriebe(currentTarget.value)}
                                        style={{paddingBottom: "10px"}}
                                    />
                                    <EATransferList
                                        id={"betriebeList"}
                                        allItems={filteredBetriebe}
                                        selectedItems={intersection(filteredBetriebe, selectedBetriebe)}
                                        setSelectedItems={(newSelectedItems) => {
                                            setSelectedBetriebe([
                                                ...not(selectedBetriebe, filteredBetriebe),
                                                ...intersection(newSelectedItems, filteredBetriebe),
                                            ]);
                                        }}
                                        listCaption="Bezeichnung"
                                        titleProperty="bezeichnung"
                                    />
                                </EAAccordion>
                            </div>
                            <div style={{gridColumnEnd: "span 2"}}>
                                <EAAccordion
                                    label={`Organisationen (${selectedOrganisationen.length})`}
                                    disabled={data.alleRegionalstellen}
                                    className="gridl"
                                    id={"organisationenAccordion"}
                                    variant="light"
                                    expanded={expandedOrganisationen && !data.alleRegionalstellen}
                                    onChange={(e, expanded) => setExpandedOrganisationen(expanded)}
                                >
                                    <EATextField
                                        id={"searchFieldOrganisationen"}
                                        value={filterOrganisationen}
                                        label={"Suche"}
                                        onChange={({currentTarget}) => setFilterOrganisationen(currentTarget.value)}
                                        style={{paddingBottom: "10px"}}
                                    />
                                    <EATransferList
                                        id={"organisationenList"}
                                        allItems={filteredOrganisationen}
                                        selectedItems={intersection(filteredOrganisationen, selectedOrganisationen)}
                                        setSelectedItems={(newSelectedItems) => {
                                            setSelectedOrganisationen([
                                                ...not(selectedOrganisationen, filteredOrganisationen),
                                                ...intersection(newSelectedItems, filteredOrganisationen),
                                            ]);
                                        }}
                                        listCaption="Bezeichnung"
                                        titleProperty="bezeichnung"
                                    />
                                </EAAccordion>
                            </div>
                            <div style={{gridColumnEnd: "span 2"}}>
                                <EAAccordion
                                    label={`Regionalstellen (${selectedRegionalstellen.length})`}
                                    className="gridl"
                                    id={"regionalstellenAccordion"}
                                    variant="light"
                                    expanded={expandedRegionalstellen && !data.alleRegionalstellen}
                                    disabled={data.alleRegionalstellen}
                                    onChange={(e, expanded) => setExpandedRegionalstellen(expanded)}
                                >
                                    <EATextField
                                        id={"searchFieldRegionalstellen"}
                                        value={filterRegionalstellen}
                                        label={"Suche"}
                                        onChange={({currentTarget}) => setFilterRegionalstellen(currentTarget.value)}
                                        style={{paddingBottom: "10px"}}
                                    />
                                    <EATransferList
                                        id={"regionalstellenList"}
                                        allItems={filteredRegionalstellen}
                                        selectedItems={intersection(filteredRegionalstellen, selectedRegionalstellen)}
                                        setSelectedItems={(newSelectedItems) => {
                                            setSelectedRegionalstellen([
                                                ...not(selectedRegionalstellen, filteredRegionalstellen),
                                                ...intersection(newSelectedItems, filteredRegionalstellen),
                                            ]);
                                        }}
                                        listCaption="Bezeichnung"
                                        titleProperty="value"
                                    />
                                </EAAccordion>
                            </div>
                        </div>
                    )}
                </div>
            }
            buttons={
                !saving && (
                    <EAButtonBar>
                        <EAButton id={"btnAbbrechen"} data-cy={"btnAbbrechen"} label={"Abbrechen"} onClick={onClickCancel}/>
                        <EAButton
                            id={"btnOk"}
                            data-cy={"btnOk"}
                            color="primary"
                            label={"Übernehmen"}
                            onClick={() => {
                                onClickOk(
                                    _.cloneDeep({
                                        betriebe: selectedBetriebe,
                                        organisationen: selectedOrganisationen,
                                        regionalstellen: selectedRegionalstellen,
                                    })
                                );
                            }}
                        />
                    </EAButtonBar>
                )
            }
        />
    );
}

TableDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    saving: PropTypes.bool.isRequired,
    data: PropTypes.any.isRequired,
    formFactory: PropTypes.any.isRequired,
    onClickCancel: PropTypes.func.isRequired,
    onClickOk: PropTypes.func.isRequired,
    betriebe: PropTypes.array.isRequired,
    organisationen: PropTypes.array.isRequired,
    regionalstellen: PropTypes.array.isRequired,
};

const RollenComponent = () => {
    const confirm = useConfirmation();
    const [{data, loading}, refetch] = usePreformattedHttp(
        (dataToMap) =>
            dataToMap.map((item) => {
                item.globaleRolle = globaleRollen.find((rolle) => rolle.id === item.globaleRolle.id);
                return item;
            }),
        getLokaleRollenEndpoint
    );
    const [{data: betriebe, loading: bLoading}] = useHttp(getBetriebAuswahlEndpoint());
    const [{data: organisationen, loading: oLoading}] = useHttp(getAlleOrganisationenEndpoint);
    const [{data: regionalstellen, loading: vLoading}] = useHttp(getRegionalstelleEndpoint);
    const [selected, setSelected] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [filter, setFilter] = useState("");
    const [filterOrganisationsrollen, setFilterOrganisationsrollen] = useState(true);
    const initialObject = {
        id: null,
        bezeichnung: "",
        geaendertAm: new Date(),
        globaleRolle: null,
        betriebe: [],
        organisationen: [],
        regionalstelleIds: [],
    };
    const schema = {
        bezeichnung: requiredString(),
        globaleRolle: requiredEnumWerteListeObjekt(),
    };
    const {values, setValues, setErrors, formFactory, submit, submitLoading: saving} = useForm({
        schema,
        readonly: isDisabled(),
    });
    const [dlgOpen, setDlgOpen] = useState(false);
    useEffect(() => {
        if (data) {
            setFilteredData([
                ...data.filter((item) => {
                    return (
                        (
                            !filter ||
                            item?.bezeichnung?.toString().toLowerCase().includes(filter.toLowerCase()) ||
                            item?.globaleRolle?.title.toLowerCase().includes(filter.toLowerCase())
                        ) && (
                            !filterOrganisationsrollen ||
                            ((filterOrganisationsrollen && (item?.systemrolle === false && item?.globaleRolle?.id === "ORGANISATION")) ||
                            item?.globaleRolle?.id === "BEHOERDE")
                        )

                    );
                }),
            ]);
        }
    }, [data, filter, loading, filterOrganisationsrollen]);

    /**
     * Validierungsfehler zurücksetzten wenn Dialog geöffnet wird
     */
    useEffect(() => {
        setErrors({});
    }, [dlgOpen, setErrors])

    function handleEdit(index) {
        if (isDisabled()) {
            return;
        }
        setValues(filteredData.find((o) => o.id === index));
        setErrors(null);
        setDlgOpen(true);
    }

    async function handleDelete(index) {
        if (isDisabled()) {
            return;
        }

        confirm(
            confirmDeleteOption(
                "die Rolle",
                () => deleteRolle(index, false),
                () => {
                }
            )
        );
    }

    async function deleteRolle(index, ignoreWarning = false) {
        await http
            .delete(getLokaleRollenEndpoint + filteredData.find((o) => o.id === index).id, {params: {ignoreWarning}})
            .then(() => {
                toast.success("Die Rolle wurde erfolgreich gelöscht.");
                setValues(initialObject);
                refetch();
            })
            .catch((e) => {
                if (e?.response?.data?.possibleSuppressorFlag === "ignoreWarning") {
                    confirm({
                        title: "Achtung, letzte lokale Rolle!",
                        description: e.response.data.message,
                        onSubmit: () => deleteRolle(index, true),
                        onClose: () => {
                        },
                    });
                } else if (!isApiCancel(e)) {
                    handleApiException(e, "Fehler beim Löschen");
                }
            });
    }

    const prepareForUpload = useCallback((clone, additionalData) => {
        if (clone.alleRegionalstellen) {
            delete clone.betriebe;
            delete clone.organisationen;
            delete clone.regionalstelleIds;
            return clone;
        }
        clone.betriebe = additionalData.betriebe.map((v) => v.id);
        clone.organisationen = additionalData.organisationen.map((v) => v.id);
        clone.regionalstelleIds = additionalData.regionalstellen.map((v) => v.id);
        return clone;
    }, []);

    function save(dataToSave) {
        const url = values.id ? getLokaleRollenEndpoint + values.id : getLokaleRollenEndpoint;
        const method = values.id ? "PUT" : "POST";
        submit((clone) => prepareForUpload(clone, dataToSave), url, method)
            .then(() => {
                toast.success("Gespeichert");
                refetch();
                setValues(initialObject);
                setDlgOpen(false);
            })
            .catch((e) => handleApiException(e, "Error saving lokale Rolle"));
    }

    function isDisabled() {
        return !userHasAnyRole([FACHADMIN, SYSTEMADMIN]) || anyLoading();
    }

    function anyLoading() {
        return loading || bLoading || oLoading || vLoading;
    }

    const columns = [
        createSelectRowComponentColumn("lokaleRollenTable", "id", data, selected, setSelected),
        createComponentColumn(
            "actions",
            (row, index) => {
                if (isDisabled()) return <div/>;
                return (
                    <div className="eatabwrapper">
                        <EATableActionButton id={"edit-" + index} iconName="ico-edit">
                            <span
                                onClick={() => handleEdit(row.id)}
                                className={"w-100"}
                                style={{
                                    height: "100%",
                                    display: "block",
                                }}
                            />
                        </EATableActionButton>
                        {!row.systemrolle && (
                            <EATableActionButton id={"delete-" + index} iconName="ico-delete">
                                <span
                                    onClick={() => handleDelete(row.id)}
                                    className={"w-100"}
                                    style={{
                                        height: "100%",
                                        display: "block",
                                    }}
                                />
                            </EATableActionButton>
                        )}
                    </div>
                );
            },
            "",
            null,
            {padding: 0, width: "50px", minWidth: "50px", maxWidth: "50px"}
        ),
        createColumn("bezeichnung", "Bezeichnung"),
        createColumn("globaleRolle.value", "Globale Rolle"),
        createCalculatedColumn(
            "geaendertAm",
            (row) => {
                return row.geaendertAm ? formatDate(row.geaendertAm) : "";
            },
            "Geändert am"
        ),
    ];

    return (
        <div className={"tabsheet-content-margin gridl"}>
            {dlgOpen && !anyLoading() && (
                <TableDialog
                    open={dlgOpen}
                    saving={saving}
                    data={{...values}}
                    formFactory={formFactory}
                    betriebe={betriebe}
                    organisationen={organisationen}
                    regionalstellen={regionalstellen}
                    onClickOk={(dataToSave) => {
                        save(dataToSave);
                    }}
                    onClickCancel={() => setDlgOpen(false)}
                />
            )}
            <div className="gridl rollenListe-filter">
                <EATextField
                    id={"searchField"}
                    disabled={anyLoading()}
                    loading={anyLoading()}
                    value={filter}
                    label={"Suche"}
                    onChange={({currentTarget}) => {
                        setFilter(currentTarget.value);
                    }}
                    style={{paddingBottom: "10px"}}
                />
                <EACheckbox
                    id="hideOrganisationsrollen"
                    onChange={(event, value) => (setFilterOrganisationsrollen(!value))}
                >
                    automatisch erzeugte Rollen anzeigen (Organisationen)
                </EACheckbox>
            </div>
            <EATable
                id={"lokaleRollenTable"}
                loading={anyLoading()}
                initialSortColumn="bezeichnung"
                columns={columns}
                data={filteredData}
            />
            {!anyLoading() && !isDisabled() && (
                <EAButtonBar>
                    <EAButton
                        id={"exportRollen"}
                        loading={anyLoading()}
                        label={"Excel-Export"}
                        onClick={() => {
                            toast.info("Noch ohne Funktion");
                        }}
                        style={{marginRight: "auto"}}
                    />
                    <EAButton
                        id={"addRolle"}
                        loading={anyLoading()}
                        label={"Hinzufügen"}
                        onClick={() => {
                            setValues(initialObject);
                            setDlgOpen(true);
                        }}
                        style={{justifySelf: "end"}}
                        color="primary"
                    />
                </EAButtonBar>
            )}
        </div>
    );
};

export default RollenComponent;
